import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { qk } from "../../lib/queryKeys";
import {
  connectSocket,
  getSocket,
  subscribeToCentinela,
  type CentinelaSocketEvent,
} from "../../socket";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useCompanyDemoFlag } from "../companies/demoFlags";
import { useCentinelaLiveStore } from "./liveStore";
import type { CentinelaUnit, CentinelaUnitsResponse } from "./types";

export function useCentinelaSocket() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const demo = useCompanyDemoFlag("centinela_demo");
  const startCall = useCentinelaLiveStore((state) => state.startCall);
  const endCall = useCentinelaLiveStore((state) => state.endCall);
  const setSubscribed = useCentinelaLiveStore((state) => state.setSubscribed);

  useEffect(() => {
    if (!companyId) return;

    const socket = connectSocket();

    const patchUnit = (deviceId: string | null, patch: Partial<CentinelaUnit>) => {
      if (!deviceId) return;
      queryClient.setQueryData<CentinelaUnitsResponse>(
        qk.centinela.units(companyId, demo),
        (current) =>
          current
            ? {
                ...current,
                units: current.units.map((unit) =>
                  unit.device_id === deviceId ? { ...unit, ...patch } : unit,
                ),
              }
            : current,
      );
    };

    const resyncUnits = async () => {
      const queryKey = qk.centinela.units(companyId, demo);
      await queryClient.cancelQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey });
    };

    const markUnitsStale = () => {
      queryClient.invalidateQueries({
        queryKey: qk.centinela.units(companyId, demo),
        refetchType: "none",
      });
    };

    const markSummaryStale = () => {
      queryClient.invalidateQueries({
        queryKey: qk.centinela.summary(companyId, demo),
        refetchType: "none",
      });
    };

    const invalidateEvents = (deviceId: string | null) => {
      if (!deviceId) return;
      queryClient.invalidateQueries({
        queryKey: qk.centinela.unitEvents(companyId, deviceId, demo),
      });
    };

    const invalidateCases = () => {
      queryClient.invalidateQueries({ queryKey: ["centinela", "cases", companyId] });
    };

    const invalidateCalls = (deviceId: string | null) => {
      if (!deviceId) return;
      queryClient.invalidateQueries({
        queryKey: qk.centinela.unitCalls(companyId, deviceId, demo),
      });
    };

    const onSubscribed = ({ companyIds }: { companyIds: string[] }) => {
      setSubscribed(companyIds);
    };

    const onEvent = (event: CentinelaSocketEvent) => {
      if (event.company_id !== companyId) return;

      switch (event.tipo) {
        case "unidad.estado":
          patchUnit(event.device_id, event.data as Partial<CentinelaUnit>);
          markSummaryStale();
          break;
        case "caso.abierto":
        case "caso.actualizado":
          patchUnit(event.device_id, { open_case_id: event.case_id });
          invalidateEvents(event.device_id);
          invalidateCases();
          markUnitsStale();
          markSummaryStale();
          break;
        case "caso.cerrado":
          patchUnit(event.device_id, { open_case_id: null });
          invalidateEvents(event.device_id);
          invalidateCases();
          markUnitsStale();
          markSummaryStale();
          break;
        case "llamada.iniciada":
          startCall(event.device_id);
          break;
        case "llamada.contestada":
          invalidateCalls(event.device_id);
          break;
        case "llamada.terminada":
          endCall(event.device_id);
          invalidateCalls(event.device_id);
          markSummaryStale();
          break;
        default:
          break;
      }
    };

    const onConnect = () => {
      subscribeToCentinela([companyId]);
      void resyncUnits();
    };

    socket.on("connect", onConnect);
    socket.on("centinela_subscribed", onSubscribed);
    socket.on("centinela_event", onEvent);

    socket.connected && subscribeToCentinela([companyId]);

    return () => {
      const current = getSocket();
      current.off("connect", onConnect);
      current.off("centinela_subscribed", onSubscribed);
      current.off("centinela_event", onEvent);
    };
  }, [companyId, demo, queryClient, startCall, endCall, setSubscribed]);
}
